import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {getCatalogFilters, type CatalogFilterGroup, type PriceRange, type ProductListParams, type SearchCategoryFacet, type SearchCategoryFilter} from "@zaaaz/shared";
import {useI18n} from "./I18nProvider";
import {CategoryMenu} from "./CategoryMenu";
import {SearchCategoryFilters} from "./SearchCategoryFilters";

export type FilterChange = {
  filters: Record<string, number[]>;
  pret: number[];
  pretMin?: number;
  pretMax?: number;
};

type Props = {
  params: ProductListParams;
  filters: Record<string, number[]>;
  pretMin?: number;
  pretMax?: number;
  onChange: (next: FilterChange) => void;
  onMeta?: (groups: CatalogFilterGroup[]) => void;
  showCategories?: boolean;
  hideBrandFilter?: boolean;
  /** When true, clear facet options while reloading (search overlay). */
  resetWhileLoading?: boolean;
  categoryFacets?: SearchCategoryFacet[];
  categoryFilter?: SearchCategoryFilter | null;
  onCategoryChange?: (next: SearchCategoryFilter | null) => void;
  categoryFacetsLoading?: boolean;
};

function countActive(filters: Record<string, number[]>, pretMin?: number, pretMax?: number) {
  let n = 0;
  if(pretMin !== undefined || pretMax !== undefined) n += 1;
  for(const k of Object.keys(filters)) n += filters[k]?.length ?? 0;
  return n;
}

function sectionActiveCount(group: CatalogFilterGroup, filters: Record<string, number[]>) {
  return (filters[String(group.id)] || []).length;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

const BRAND_TOGGLE_MIN = 5;

export function ProductFilters({params, filters, pretMin, pretMax, onChange, onMeta, showCategories = true, hideBrandFilter = false, resetWhileLoading = false, categoryFacets, categoryFilter, onCategoryChange, categoryFacetsLoading = false}: Props) {
  const {t} = useI18n();
  const [groups, setGroups] = useState<CatalogFilterGroup[]>([]);
  const [priceRange, setPriceRange] = useState<PriceRange | null>(null);
  const [loading, setLoading] = useState(false);
  const [brandQ, setBrandQ] = useState("");
  const [brandExpanded, setBrandExpanded] = useState(false);
  const loadSeq = useRef(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [sliderLo, setSliderLo] = useState(0);
  const [sliderHi, setSliderHi] = useState(1000);
  const [rangeMin, setRangeMin] = useState("");
  const [rangeMax, setRangeMax] = useState("");
  const listParams = useMemo(() => ({...params, filters, pret: [], pretMin, pretMax}), [params, filters, pretMin, pretMax]);
  const visibleGroups = useMemo(() => groups.filter((g) => !(hideBrandFilter && g.isBrand)), [groups, hideBrandFilter]);
  const load = useCallback(() => {
    const seq = ++loadSeq.current;
    setLoading(true);
    if(resetWhileLoading) {
      setGroups([]);
      setPriceRange(null);
    }
    getCatalogFilters(listParams).then((r) => {
      if(seq !== loadSeq.current) return;
      setGroups(r.filters);
      setPriceRange(r.priceRange);
      onMeta?.(r.filters.filter((g) => !(hideBrandFilter && g.isBrand)));
    }).catch(() => {
      if(seq !== loadSeq.current) return;
      if(resetWhileLoading) {
        setGroups([]);
        setPriceRange(null);
      }
    }).finally(() => {
      if(seq === loadSeq.current) setLoading(false);
    });
  }, [JSON.stringify(listParams), onMeta, hideBrandFilter, resetWhileLoading]);
  useEffect(() => {
    if(resetWhileLoading && params.s) {
      const t = setTimeout(load, 180);
      return () => clearTimeout(t);
    }
    load();
  }, [load, resetWhileLoading, params.s]);
  useEffect(() => { setBrandQ(""); setBrandExpanded(false); }, [params.s]);
  useEffect(() => {
    if(!onCategoryChange) return;
    setOpenSections((prev) => (prev.searchCats === undefined ? {...prev, searchCats: true} : prev));
  }, [onCategoryChange, categoryFacets?.length]);
  useEffect(() => {
    if(!priceRange) return;
    const lo = pretMin !== undefined ? pretMin : priceRange.min;
    const hi = pretMax !== undefined ? pretMax : priceRange.max;
    setSliderLo(clamp(lo, priceRange.min, priceRange.max));
    setSliderHi(clamp(hi, priceRange.min, priceRange.max));
    setRangeMin(pretMin !== undefined ? String(pretMin) : "");
    setRangeMax(pretMax !== undefined ? String(pretMax) : "");
  }, [priceRange, pretMin, pretMax]);
  useEffect(() => {
    setOpenSections((prev) => {
      const next = {...prev};
      for(const g of visibleGroups) {
        if(next[`g${g.id}`] === undefined) next[`g${g.id}`] = true;
      }
      if(priceRange && next.price === undefined) next.price = true;
      if(showCategories && next.categories === undefined) next.categories = true;
      return next;
    });
  }, [visibleGroups, priceRange, showCategories]);
  const activeTotal = useMemo(() => countActive(filters, pretMin, pretMax) + (categoryFilter?.c ? 1 : 0), [filters, pretMin, pretMax, categoryFilter]);
  const hasCustomRange = pretMin !== undefined || pretMax !== undefined;
  function emit(next: FilterChange) {
    onChange(next);
  }
  function toggleFilter(fid: number, oid: number) {
    const key = String(fid);
    const cur = filters[key] ? [...filters[key]] : [];
    const idx = cur.indexOf(oid);
    if(idx >= 0) cur.splice(idx, 1);
    else cur.push(oid);
    const nextFilters = {...filters};
    if(cur.length) nextFilters[key] = cur;
    else delete nextFilters[key];
    emit({filters: nextFilters, pret: [], pretMin, pretMax});
  }
  function syncSliderLo(v: number) {
    if(!priceRange) return;
    const next = clamp(v, priceRange.min, sliderHi);
    setSliderLo(next);
    setRangeMin(String(next));
  }
  function syncSliderHi(v: number) {
    if(!priceRange) return;
    const next = clamp(v, sliderLo, priceRange.max);
    setSliderHi(next);
    setRangeMax(String(next));
  }
  function applyPriceRange() {
    if(!priceRange) return;
    const minRaw = rangeMin.trim() ? Number(rangeMin) : sliderLo;
    const maxRaw = rangeMax.trim() ? Number(rangeMax) : sliderHi;
    if(Number.isNaN(minRaw) || Number.isNaN(maxRaw)) return;
    const min = clamp(minRaw, priceRange.min, priceRange.max);
    const max = clamp(maxRaw, priceRange.min, priceRange.max);
    if(min > max) return;
    if(min <= priceRange.min && max >= priceRange.max) {
      emit({filters, pret: [], pretMin: undefined, pretMax: undefined});
      return;
    }
    emit({filters, pret: [], pretMin: min, pretMax: max});
  }
  function clearAll() {
    if(priceRange) {
      setSliderLo(priceRange.min);
      setSliderHi(priceRange.max);
    }
    setRangeMin("");
    setRangeMax("");
    emit({filters: {}, pret: [], pretMin: undefined, pretMax: undefined});
    setBrandQ("");
    onCategoryChange?.(null);
  }
  function toggleSection(key: string) {
    setOpenSections((prev) => ({...prev, [key]: !prev[key]}));
  }
  const showPrice = priceRange && priceRange.max > priceRange.min;
  const showSkeleton = loading && (resetWhileLoading || !groups.length);
  const priceSection = !showSkeleton && showPrice ? (
    <div className="cc-filters__section">
      <button type="button" className="cc-filters__sectionHead" onClick={() => toggleSection("price")} aria-expanded={!!openSections.price}>
        <span>{t("PRICE")}</span>
        <span className="cc-filters__sectionMeta">
          {hasCustomRange ? <span className="cc-filters__sectionBadge">1</span> : null}
          <span className="cc-filters__sectionToggle" aria-hidden="true">{openSections.price ? "−" : "+"}</span>
        </span>
      </button>
      {openSections.price ? (
        <div className="cc-filters__sectionBody">
          <div className="cc-filters__priceRange">
            <div className="cc-filters__slider">
              <div className="cc-filters__sliderTrack">
                <div
                  className="cc-filters__sliderFill"
                  style={{
                    left: `${((sliderLo - priceRange!.min) / (priceRange!.max - priceRange!.min)) * 100}%`,
                    right: `${100 - ((sliderHi - priceRange!.min) / (priceRange!.max - priceRange!.min)) * 100}%`
                  }}
                />
              </div>
              <input
                type="range"
                className="cc-filters__sliderInput cc-filters__sliderInput--lo"
                min={priceRange!.min}
                max={priceRange!.max}
                step={1}
                value={sliderLo}
                onChange={(e) => syncSliderLo(Number(e.target.value))}
                onMouseUp={applyPriceRange}
                onTouchEnd={applyPriceRange}
                aria-label={t("Min")}
              />
              <input
                type="range"
                className="cc-filters__sliderInput cc-filters__sliderInput--hi"
                min={priceRange!.min}
                max={priceRange!.max}
                step={1}
                value={sliderHi}
                onChange={(e) => syncSliderHi(Number(e.target.value))}
                onMouseUp={applyPriceRange}
                onTouchEnd={applyPriceRange}
                aria-label={t("Max")}
              />
            </div>
            <div className="cc-filters__priceFields">
              <div className="cc-filters__priceField">
                <span className="cc-filters__priceFieldLabel">{t("Min")}</span>
                <input
                  type="number"
                  min={priceRange!.min}
                  max={priceRange!.max}
                  className="cc-filters__priceInput"
                  placeholder={String(priceRange!.min)}
                  value={rangeMin}
                  onChange={(e) => {
                    setRangeMin(e.target.value);
                    const n = Number(e.target.value);
                    if(!Number.isNaN(n)) syncSliderLo(n);
                  }}
                  onBlur={applyPriceRange}
                  onKeyDown={(e) => { if(e.key === "Enter") applyPriceRange(); }}
                />
              </div>
              <div className="cc-filters__priceField">
                <span className="cc-filters__priceFieldLabel">{t("Max")}</span>
                <input
                  type="number"
                  min={priceRange!.min}
                  max={priceRange!.max}
                  className="cc-filters__priceInput"
                  placeholder={String(priceRange!.max)}
                  value={rangeMax}
                  onChange={(e) => {
                    setRangeMax(e.target.value);
                    const n = Number(e.target.value);
                    if(!Number.isNaN(n)) syncSliderHi(n);
                  }}
                  onBlur={applyPriceRange}
                  onKeyDown={(e) => { if(e.key === "Enter") applyPriceRange(); }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  ) : null;
  return (
    <aside className={`cc-filters${mobileOpen ? " isOpen" : ""}${showSkeleton ? " isLoading" : ""}`} aria-label={t("FILTER PRODUCTS")}>
      <button type="button" className="cc-filters__mobileToggle" onClick={() => setMobileOpen(!mobileOpen)}>
        <span>{t("FILTER PRODUCTS")}{activeTotal > 0 ? ` (${activeTotal})` : ""}</span>
        <i className={`fa fa-chevron-${mobileOpen ? "up" : "down"}`} aria-hidden="true" />
      </button>
      <div className="cc-filters__panel">
        <div className="cc-filters__head">
          <h2 className="cc-filters__title">{t("FILTER PRODUCTS")}</h2>
          {activeTotal > 0 ? (
            <div className="cc-filters__headActions">
              <span className="cc-filters__count">{activeTotal}</span>
              <button type="button" className="cc-filters__clear" onClick={clearAll}>{t("Clear filters")}</button>
            </div>
          ) : null}
        </div>
        <div className="cc-filters__body">
          {showSkeleton ? (
            <div className="cc-searchOverlay__filtSk" aria-hidden="true">
              <div className="cc-searchOverlay__filtSkHead" />
              <div className="cc-searchOverlay__filtSkBlock">
                <div className="cc-searchOverlay__filtSkLine" />
                <div className="cc-searchOverlay__filtSkLine cc-searchOverlay__filtSkLine--short" />
                <div className="cc-searchOverlay__filtSkLine cc-searchOverlay__filtSkLine--short" />
                <div className="cc-searchOverlay__filtSkLine cc-searchOverlay__filtSkLine--short" />
              </div>
              <div className="cc-searchOverlay__filtSkBlock">
                <div className="cc-searchOverlay__filtSkLine" />
                <div className="cc-searchOverlay__filtSkLine cc-searchOverlay__filtSkLine--short" />
                <div className="cc-searchOverlay__filtSkLine cc-searchOverlay__filtSkLine--short" />
              </div>
            </div>
          ) : null}
          {priceSection}
          {!showSkeleton && showCategories ? (
            <CategoryMenu filterScoped open={!!openSections.categories} onToggle={() => toggleSection("categories")} />
          ) : null}
          {!showSkeleton && onCategoryChange ? (
            <SearchCategoryFilters
              facets={categoryFacets ?? []}
              selected={categoryFilter ?? null}
              loading={categoryFacetsLoading}
              open={!!openSections.searchCats}
              onToggle={() => toggleSection("searchCats")}
              onChange={onCategoryChange}
            />
          ) : null}
          {!showSkeleton ? visibleGroups.map((g) => {
            const key = `g${g.id}`;
            const open = !!openSections[key];
            const opts = g.isBrand && brandQ
              ? g.options.filter((o) => o.nume.toLowerCase().includes(brandQ.toLowerCase()))
              : g.options;
            const brandListExpanded = brandExpanded || !!brandQ.trim();
            const showBrandToggle = g.isBrand && !brandQ && opts.length > BRAND_TOGGLE_MIN;
            const sel = sectionActiveCount(g, filters);
            return (
              <div key={g.id} className="cc-filters__section">
                <button type="button" className="cc-filters__sectionHead" onClick={() => toggleSection(key)} aria-expanded={open}>
                  <span>{g.nume}</span>
                  <span className="cc-filters__sectionMeta">
                    {sel > 0 ? <span className="cc-filters__sectionBadge">{sel}</span> : null}
                    <span className="cc-filters__sectionToggle" aria-hidden="true">{open ? "−" : "+"}</span>
                  </span>
                </button>
                {open ? (
                  <div className="cc-filters__sectionBody">
                    {g.isBrand ? (
                      <div className="cc-filters__search">
                        <input
                          type="search"
                          className="cc-filters__searchInput"
                          placeholder={t("Search for brand...")}
                          value={brandQ}
                          onChange={(e) => setBrandQ(e.target.value)}
                        />
                      </div>
                    ) : null}
                    <div className={`cc-filters__options${g.isBrand ? ` cc-filters__options--brandList${brandListExpanded ? " isExpanded" : " isCollapsed"}` : ""}`}>
                      {opts.length ? opts.map((o) => {
                        const active = (filters[String(g.id)] || []).includes(o.id);
                        return (
                          <button key={o.id} type="button" className={`cc-filters__option${active ? " isActive" : ""}`} onClick={() => toggleFilter(g.id, o.id)} aria-pressed={active}>
                            <span className="cc-filters__optionBox" aria-hidden="true" />
                            <span className="cc-filters__optionLabel">{o.nume}</span>
                          </button>
                        );
                      }) : (
                        <p className="cc-filters__empty">{t("No products found")}</p>
                      )}
                    </div>
                    {showBrandToggle ? (
                      <button type="button" className="cc-filters__viewToggle" onClick={() => setBrandExpanded((v) => !v)}>
                        {brandExpanded ? t("View less") : t("View more")}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          }) : null}
        </div>
      </div>
    </aside>
  );
}
